// src/pages/Account.jsx — v26
// CHANGED FROM v25 (22 Jul 2026): REAL FIX — the tier progress bar's
// fill percentage was computed from displayWallet.lifetimeSpend (true
// all-time spend, never resets), which would have kept showing the
// wrong tier position even after Code.gs v56 correctly switched tier
// calculation to calendar-year spend. computeTierFillPct() now uses
// displayWallet.yearSpend instead. Added yearSpend: 0 to the
// signed-out/no-wallet fallback object for consistency.
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import BottomNav from '../components/BottomNav';
import { useStore } from '../context/StoreContext';
const TIER_META = [
  { name: 'Just Arrived',     min: 0,     bg: '#E6F1FB', fill: '#0C447C', text: '#0C447C' },
  { name: 'Getting Comfy',    min: 5000,  bg: '#B5D4F4', fill: '#185FA5', text: '#185FA5' },
  { name: 'Part of the Gang', min: 15000, bg: '#85B7EB', fill: '#185FA5', text: '#185FA5' },
  { name: 'Inner Circle',     min: 30000, bg: '#378ADD', fill: '#185FA5', text: '#185FA5' },
  { name: 'nobrand OG',       min: 50000, bg: '#042C53', fill: '#042C53', text: '#042C53' },
];
const OG_VISUAL_WIDTH = 20000;
const MSG91_WIDGET_ID = '36676469546e333134393136';
const MSG91_TOKEN_AUTH = '547390TKaZu5sG16a48d72dP1';
const ADMIN_EMAIL = 'nobrandstore3@gmail.com';
export default function Account() {
  const { user, logout, savedAddresses, saveWhatsappNumber } = useStore();
  const navigate = useNavigate();
  const [editingPhone, setEditingPhone] = useState(false);
  const [phoneOtpPhase, setPhoneOtpPhase] = useState('enter-phone');
  const [phoneEditValue, setPhoneEditValue] = useState('');
  const [phoneOtpValue, setPhoneOtpValue] = useState('');
  const [phoneBusy, setPhoneBusy] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteAck, setDeleteAck] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [deleteCountdown, setDeleteCountdown] = useState(10);
  const widgetReady = useRef(false);
  useEffect(() => {
    if (widgetReady.current) return;
    window.__msg91ConfigAccount = {
      widgetId: MSG91_WIDGET_ID,
      tokenAuth: MSG91_TOKEN_AUTH,
      exposeMethods: true,
      success: () => {},
      failure: (err) => {
        console.error('MSG91 widget-level failure (Account):', err);
      },
    };
    (function loadOtpScript(urls) {
      let i = 0;
      function attempt() {
        const s = document.createElement('script');
        s.src = urls[i];
        s.async = true;
        s.onload = () => {
          if (typeof window.initSendOTP === 'function') {
            window.initSendOTP(window.__msg91ConfigAccount);
            widgetReady.current = true;
          }
        };
        s.onerror = () => {
          i++;
          if (i < urls.length) attempt();
        };
        document.head.appendChild(s);
      }
      attempt();
    })(['https://verify.msg91.com/otp-provider.js', 'https://verify.phone91.com/otp-provider.js']);
  }, []);
  useEffect(() => {
    if (!showDeleteModal) return;
    setDeleteCountdown(10);
    const interval = setInterval(() => {
      setDeleteCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [showDeleteModal]);
  if (!user) return (
    <div className="app-shell">
      <Navbar />
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '80px 20px', textAlign: 'center' }}>
        <i className="ti ti-user" style={{ fontSize: 52, color: 'var(--muted)', marginBottom: 16 }}></i>
        <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 8 }}>Not signed in</div>
        <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 24 }}>Sign in to view your orders and saved addresses</div>
        <button className="btn-primary" style={{ width: 180 }} onClick={() => navigate('/login')}>Sign in</button>
      </div>
      <BottomNav />
    </div>
  );
  const initials = user.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  const wallet = user.wallet;
  const isAdmin = user.email === ADMIN_EMAIL;
  const displayWallet = wallet || {
    tier: 'Just Arrived',
    walletBalance: 0,
    lifetimeSpend: 0,
    yearSpend: 0,
    nextTierName: 'Getting Comfy',
    amountToNextTier: 5000,
    earnRate: 0.01,
    isRedeemable: false,
  };
  function openPhoneEdit() {
    setEditingPhone(true);
    setPhoneOtpPhase('enter-phone');
    setPhoneEditValue('');
    setPhoneOtpValue('');
    setPhoneError('');
  }
  function cancelPhoneEdit() {
    setEditingPhone(false);
    setPhoneOtpPhase('enter-phone');
    setPhoneEditValue('');
    setPhoneOtpValue('');
    setPhoneError('');
  }
  function maskOldPhone() {
    const digits = String(user.phone || '').replace(/\D/g, '');
    if (digits.length < 4) return user.phone || '';
    return 'XXXXXX' + digits.slice(-4);
  }
  function handleSendPhoneOtp() {
    setPhoneError('');
    if (!phoneEditValue.trim() || phoneEditValue.trim().length < 10) {
      setPhoneError('Please enter a valid 10-digit mobile number');
      return;
    }
    if (!window.sendOtp) {
      setPhoneError('OTP service not ready yet. Please wait a moment and try again.');
      return;
    }
    setPhoneBusy(true);
    const otpTargetDigits = user.phone
      ? String(user.phone).replace(/\D/g, '').slice(-10)
      : phoneEditValue.trim().replace(/\D/g, '').slice(-10);
    const identifier = '91' + otpTargetDigits;
    window.sendOtp(
      identifier,
      () => {
        setPhoneBusy(false);
        setPhoneOtpPhase('enter-otp');
      },
      (err) => {
        console.error('MSG91 sendOtp failure (Account):', err);
        setPhoneBusy(false);
        setPhoneError('Could not send OTP. Please try again.');
      }
    );
  }
  function handleVerifyPhoneOtp() {
    setPhoneError('');
    if (!phoneOtpValue.trim() || phoneOtpValue.trim().length !== 6) {
      setPhoneError('Please enter the 6-digit code');
      return;
    }
    if (!window.verifyOtp) {
      setPhoneError('OTP service not ready. Please try again.');
      return;
    }
    setPhoneBusy(true);
    window.verifyOtp(
      phoneOtpValue.trim(),
      async (data) => {
        const accessToken = data?.message;
        if (!accessToken) {
          setPhoneBusy(false);
          setPhoneError('Verification failed. Please try again.');
          return;
        }
        try {
          const res = await fetch('/.netlify/functions/verify-otp-simple', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ accessToken }),
          });
          const verifyData = await res.json();
          if (!res.ok || verifyData.status !== 'ok') {
            setPhoneBusy(false);
            setPhoneError(verifyData.error || 'Verification failed. Please try again.');
            return;
          }
          const result = await saveWhatsappNumber(phoneEditValue.trim());
          setPhoneBusy(false);
          if (result.error) {
            setPhoneError(result.error);
            return;
          }
          cancelPhoneEdit();
        } catch (err) {
          setPhoneBusy(false);
          setPhoneError('Network error saving number. Please try again.');
        }
      },
      (err) => {
        console.error('MSG91 verifyOtp failure (Account):', err);
        setPhoneBusy(false);
        setPhoneError('Incorrect OTP. Please try again.');
      }
    );
  }
  async function handleDeleteAccount() {
    setDeleteError('');
    setDeleteBusy(true);
    try {
      const res = await fetch('/.netlify/functions/delete-account', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid: user.uid }),
      });
      const data = await res.json();
      if (!res.ok) {
        setDeleteBusy(false);
        setDeleteError(data.error || 'Could not delete your account. Please try again or contact support.');
        return;
      }
      await logout();
      navigate('/', { state: { accountDeleted: true } });
    } catch (err) {
      setDeleteBusy(false);
      setDeleteError('Network error. Please try again or contact support.');
    }
  }
  const currentTierIndex = TIER_META.findIndex(t => t.name === displayWallet.tier);
  const safeTierIndex = currentTierIndex === -1 ? 0 : currentTierIndex;
  // CHANGED v26: uses displayWallet.yearSpend instead of
  // displayWallet.lifetimeSpend — this is what actually determines
  // the tier now, so the fill bar must match.
  function computeTierFillPct(tierIdx) {
    if (tierIdx < safeTierIndex) return 100;
    if (tierIdx > safeTierIndex) return 0;
    const spendForFill = displayWallet.yearSpend ?? displayWallet.lifetimeSpend;
    const lo = TIER_META[tierIdx].min;
    const hi = tierIdx < TIER_META.length - 1 ? TIER_META[tierIdx + 1].min : lo + OG_VISUAL_WIDTH;
    return Math.min(100, Math.max(0, ((spendForFill - lo) / (hi - lo)) * 100));
  }
  const markerLeftPct = (safeTierIndex * (100 / 5)) + (computeTierFillPct(safeTierIndex) / 100 * (100 / 5));
  const nextTierColor = displayWallet.nextTierName
    ? (TIER_META.find(t => t.name === displayWallet.nextTierName)?.text || 'var(--muted)')
    : 'var(--muted)';
  return (
    <div className="app-shell">
      <Navbar />
      <div style={{ background: 'var(--navy)', padding: '20px 18px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'var(--blue)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 500, color: '#fff', flexShrink: 0 }}>
            {initials}
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 500, color: 'var(--off-white)', marginBottom: 2 }}>{user.name}</div>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>{user.customerId || '—'}</div>
          </div>
        </div>
      </div>
      <div className="page-body">
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--blue)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
            Account details
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
              <span style={{ color: 'var(--muted)' }}>Customer ID</span>
              <span style={{ color: 'var(--navy)', fontWeight: 500 }}>{user.customerId || '—'}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
              <span style={{ color: 'var(--muted)' }}>Email</span>
              <span style={{ color: 'var(--navy)', fontWeight: 500 }}>{user.email || '—'}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12 }}>
              <span style={{ color: 'var(--muted)' }}>Phone</span>
              {!editingPhone && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ color: 'var(--navy)', fontWeight: 500 }}>{user.phone || 'Not added'}</span>
                  <button
                    onClick={openPhoneEdit}
                    style={{ background: 'none', border: 'none', color: 'var(--blue)', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--font)', padding: 0 }}
                  >
                    {user.phone ? 'Update' : 'Add'}
                  </button>
                </span>
              )}
            </div>
            {editingPhone && (
              <div style={{ marginTop: 2 }}>
                {phoneError && (
                  <div style={{ background: '#FEE2E2', color: '#B91C1C', fontSize: 11, padding: '8px 10px', borderRadius: 6, marginBottom: 8 }}>
                    {phoneError}
                  </div>
                )}
                {phoneOtpPhase === 'enter-phone' && (
                  <>
                    <input
                      type="tel"
                      placeholder="New 10-digit mobile number"
                      value={phoneEditValue}
                      onChange={e => setPhoneEditValue(e.target.value)}
                      style={{ width: '100%', padding: '10px 12px', border: '0.5px solid rgba(27,42,74,0.2)', borderRadius: 6, fontSize: 13, color: 'var(--navy)', background: '#fff', fontFamily: 'var(--font)', outline: 'none', marginBottom: 8 }}
                    />
                    <div style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 8 }}>
                      {user.phone
                        ? `To confirm it's really you, we'll text a verification code to your current number on file (ending ${maskOldPhone().slice(-4)}), not this new one.`
                        : "We'll text a verification code to this number to confirm it's yours."}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        onClick={cancelPhoneEdit}
                        style={{ flex: 1, padding: 9, background: 'transparent', border: '0.5px solid rgba(27,42,74,0.2)', color: 'var(--navy)', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontFamily: 'var(--font)' }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSendPhoneOtp}
                        disabled={phoneBusy}
                        style={{ flex: 1, padding: 9, background: 'var(--blue)', border: 'none', color: '#fff', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontFamily: 'var(--font)' }}
                      >
                        {phoneBusy ? 'Sending…' : 'Send OTP'}
                      </button>
                    </div>
                  </>
                )}
                {phoneOtpPhase === 'enter-otp' && (
                  <>
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 8 }}>
                      Code sent to {user.phone ? `your number on file (${maskOldPhone()})` : phoneEditValue}
                    </div>
                    <input
                      type="tel"
                      maxLength={6}
                      placeholder="6-digit OTP"
                      value={phoneOtpValue}
                      onChange={e => setPhoneOtpValue(e.target.value)}
                      style={{ width: '100%', padding: '10px 12px', border: '0.5px solid rgba(27,42,74,0.2)', borderRadius: 6, fontSize: 13, color: 'var(--navy)', background: '#fff', fontFamily: 'var(--font)', outline: 'none', marginBottom: 8 }}
                    />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        onClick={cancelPhoneEdit}
                        style={{ flex: 1, padding: 9, background: 'transparent', border: '0.5px solid rgba(27,42,74,0.2)', color: 'var(--navy)', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontFamily: 'var(--font)' }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleVerifyPhoneOtp}
                        disabled={phoneBusy}
                        style={{ flex: 1, padding: 9, background: 'var(--blue)', border: 'none', color: '#fff', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontFamily: 'var(--font)' }}
                      >
                        {phoneBusy ? 'Verifying…' : 'Verify & Save'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
              <span style={{ color: 'var(--muted)' }}>Account type</span>
              <span style={{ color: 'var(--navy)', fontWeight: 500 }}>{user.role || '—'}</span>
            </div>
          </div>
        </div>
        <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--blue)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                nb wallet
              </div>
              <span style={{ fontSize: 11, fontWeight: 600, background: 'var(--gold)', color: '#fff', padding: '3px 10px', borderRadius: 10 }}>
                {displayWallet.tier}
              </span>
            </div>
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 2 }}>Wallet balance</div>
              <div style={{ fontSize: 22, fontWeight: 500, color: 'var(--navy)' }}>₹{displayWallet.walletBalance.toLocaleString()}</div>
            </div>
            <div className="tier-bar-wrap">
              <div className="tier-bar-track">
                {TIER_META.map((t, i) => (
                  <div key={t.name} className="tier-seg" style={{ background: t.bg }}>
                    <div className="tier-seg-fill" style={{ width: `${computeTierFillPct(i)}%`, background: t.fill }}></div>
                  </div>
                ))}
              </div>
              {displayWallet.nextTierName && (
                <>
                  <div className="tier-marker-pct" style={{ left: `${markerLeftPct}%`, color: TIER_META[safeTierIndex].fill }}>
                    {Math.round(computeTierFillPct(safeTierIndex))}%
                  </div>
                  <div className="tier-marker" style={{ left: `${markerLeftPct}%`, background: TIER_META[safeTierIndex].fill }}>
                    <span className="tier-marker-rupee">₹</span>
                  </div>
                </>
              )}
            </div>
            <div className="tier-labels">
              {TIER_META.map((t, i) => (
                <div key={t.name} className="tier-label" style={{ color: t.text, textAlign: i === 0 ? 'left' : i === TIER_META.length - 1 ? 'right' : 'center' }}>
                  {t.name}
                </div>
              ))}
            </div>
            {displayWallet.nextTierName ? (
              <div style={{ fontSize: 11, color: nextTierColor, marginTop: 14 }}>
                ₹{displayWallet.amountToNextTier.toLocaleString()} more to reach <strong style={{ color: nextTierColor }}>{displayWallet.nextTierName}</strong>
              </div>
            ) : (
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 14 }}>You've reached the top tier — nobrand OG</div>
            )}
            <Link
              to="/cashback-program"
              style={{ fontSize: 10, color: '#854F0B', marginTop: 8, background: '#FEF8E8', padding: '6px 10px', borderRadius: 6, display: 'inline-block', textDecoration: 'none' }}
            >
              Based on items kept — returns don't count toward your tier. <strong>View Cashback Program →</strong>
            </Link>
            <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 10, marginBottom: 14 }}>
              {displayWallet.isRedeemable
                ? `Earning ${(displayWallet.earnRate * 100).toFixed(1)}% cashback on every order · Eligible to redeem (up to 50% of order value)`
                : `Earning ${(displayWallet.earnRate * 100).toFixed(1)}% cashback on every order · unlocks for use once balance exceeds ₹25`}
            </div>
            <button
              onClick={() => navigate('/account/wallet-statement')}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: 10, background: 'var(--navy)', border: 'none', color: '#fff', borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--font)' }}
            >
              <i className="ti ti-file-text" style={{ fontSize: 14 }}></i>
              View Cashback Statement
            </button>
          </div>
        <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 16 }}>
          {[
            { icon: 'ti-package', label: 'Order history', color: '#EEF4FF', iconColor: 'var(--blue)', action: () => navigate('/account/orders') },
            { icon: 'ti-map-pin', label: 'Saved addresses', color: '#EEF4FF', iconColor: 'var(--blue)', count: savedAddresses.length, action: () => navigate('/account/addresses') },
            { icon: 'ti-heart', label: 'Wishlist', color: '#FEF8E8', iconColor: 'var(--gold)', action: () => navigate('/wishlist') },
            { icon: 'ti-brand-whatsapp', label: 'Contact support', color: '#E8F5E9', iconColor: '#25D366', action: () => window.open('https://wa.me/919363012496', '_blank') },
          ].map((item, i, arr) => (
            <div
              key={i}
              onClick={item.action}
              style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', borderBottom: i < arr.length - 1 ? '0.5px solid var(--card-border)' : 'none', cursor: 'pointer' }}
            >
              <div style={{ width: 34, height: 34, borderRadius: 8, background: item.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className={`ti ${item.icon}`} style={{ fontSize: 18, color: item.iconColor }}></i>
              </div>
              <span style={{ flex: 1, fontSize: 13 }}>{item.label}</span>
              {item.count !== undefined && item.count > 0 && (
                <span style={{ fontSize: 11, background: 'var(--blue)', color: '#fff', padding: '2px 8px', borderRadius: 10 }}>{item.count}</span>
              )}
              <i className="ti ti-chevron-right" style={{ fontSize: 16, color: 'var(--muted)' }}></i>
            </div>
          ))}
        </div>
        <div className="card" style={{ padding: 12, marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button
            onClick={async () => { await logout(); alert('You have been signed out.'); navigate('/'); }}
            style={{ width: '100%', padding: 13, background: 'transparent', border: '0.5px solid rgba(226,75,74,0.3)', color: 'var(--red)', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontFamily: 'var(--font)', letterSpacing: '0.06em' }}
          >
            Sign out
          </button>
          {!isAdmin && (
            <button
              onClick={() => { setShowDeleteModal(true); setDeleteAck(false); setDeleteError(''); }}
              style={{ width: '100%', padding: 13, background: '#B91C1C', border: 'none', color: '#fff', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font)' }}
            >
              Delete my account
            </button>
          )}
        </div>
      </div>
      {showDeleteModal && !isAdmin && (
        <div
          onClick={() => !deleteBusy && setShowDeleteModal(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}
        >
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 10, padding: '1.5rem', width: '100%', maxWidth: 380 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <i className="ti ti-alert-triangle" style={{ fontSize: 22, color: '#B91C1C' }}></i>
              <span style={{ fontSize: 15, fontWeight: 700, color: '#B91C1C' }}>Delete your account?</span>
            </div>
            <div style={{ background: '#FDECEC', borderRadius: 8, padding: 12, marginBottom: 14 }}>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: '#7A2E2E', lineHeight: 1.8 }}>
                <li><strong>This is permanent and cannot be undone.</strong></li>
                <li>You will lose your account, saved addresses, and wishlist immediately.</li>
                <li>
                  Your entire <strong>nb wallet balance and all cashback will be permanently lost</strong>. It cannot be recovered, refunded, or applied to any other account, including a new account made with the same email or phone number.
                </li>
                <li>We will not keep any of your data on our end once your account is deleted.</li>
                <li>You will need to sign in again and create a new account to shop with us in future — as a brand-new customer, starting from zero.</li>
              </ul>
            </div>
            {deleteError && (
              <div style={{ background: '#FEE2E2', color: '#B91C1C', fontSize: 11, padding: '8px 10px', borderRadius: 6, marginBottom: 12 }}>
                {deleteError}
              </div>
            )}
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, color: deleteCountdown > 0 ? 'var(--muted)' : 'var(--navy)', cursor: deleteCountdown > 0 ? 'not-allowed' : 'pointer', marginBottom: 16 }}>
              <input
                type="checkbox"
                checked={deleteAck}
                disabled={deleteCountdown > 0}
                onChange={e => setDeleteAck(e.target.checked)}
                style={{ marginTop: 2 }}
              />
              I understand this is permanent, and that my nb wallet balance will be lost for good and cannot be recovered or moved to another account.
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={deleteBusy}
                style={{ flex: 1, padding: 12, background: 'transparent', border: '0.5px solid rgba(27,42,74,0.2)', color: 'var(--navy)', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontFamily: 'var(--font)' }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={!deleteAck || deleteBusy || deleteCountdown > 0}
                style={{ flex: 1.4, padding: 12, background: (deleteAck && deleteCountdown === 0) ? '#B91C1C' : '#E5B3B3', border: 'none', color: '#fff', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: (deleteAck && deleteCountdown === 0) ? 'pointer' : 'not-allowed', fontFamily: 'var(--font)' }}
              >
                {deleteBusy ? 'Deleting…' : deleteCountdown > 0 ? `Please wait ${deleteCountdown}s…` : 'Delete my account'}
              </button>
            </div>
          </div>
        </div>
      )}
      <BottomNav />
      <style>{`
        .tier-bar-wrap { position: relative; margin-bottom: 6px; }
        .tier-bar-track { display: flex; gap: 3px; height: 14px; }
        .tier-seg { flex: 1; border-radius: 4px; overflow: hidden; }
        .tier-seg:first-child { border-radius: 7px 4px 4px 7px; }
        .tier-seg:last-child { border-radius: 4px 7px 7px 4px; }
        .tier-seg-fill { height: 100%; transition: width 0.4s ease; }
        .tier-marker {
          position: absolute;
          top: -5px;
          width: 24px;
          height: 24px;
          margin-left: -12px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 3px solid #fff;
          transition: left 0.4s ease, background 0.4s ease;
        }
        .tier-marker-rupee {
          color: #fff;
          font-size: 12px;
          font-weight: 600;
          display: inline-block;
          animation: nbTierRupeeSpin 3s linear infinite;
        }
        .tier-marker-pct {
          position: absolute;
          top: 1px;
          font-size: 10px;
          font-weight: 700;
          transform: translateX(16px);
          transition: left 0.4s ease, color 0.4s ease;
          white-space: nowrap;
        }
        @keyframes nbTierRupeeSpin {
          from { transform: rotateY(0deg); }
          to { transform: rotateY(360deg); }
        }
        .tier-labels { display: flex; gap: 3px; margin-top: 8px; }
        .tier-label { flex: 1; font-size: 9px; font-weight: 500; line-height: 1.3; }
      `}</style>
    </div>
  );
}